import { ApolloError } from '@apollo/client'
import { datadogRum } from '@datadog/browser-rum'
import { generateDataDogContext } from '../../utils/dataDog'
import { IGetOrdersOrder } from '../../graphQL/queries/getOrders/types'
import { IOrderSummary } from '../../redux/slices/orders/types'

/**
 * Maps a `getOrders` response row onto the shape `OrderCard` / `OrderList`
 * consume.
 *
 * `itemsCount` is derived here because `Order` carries no such field: the
 * query selects a minimal `items { sku }` purely so the lines can be counted.
 * An order with no lines yields `0`, not `undefined`.
 *
 * Response order is preserved rather than sorted. The user API fixes the
 * result newest-first and exposes no `sort` argument, so a client-side
 * re-sort would fight the server with nothing to signal the disagreement.
 * @internal
 */
export const toOrderSummaries = (
  orders?: IGetOrdersOrder[] | null,
): IOrderSummary[] => {
  // The GraphQL contract declares `orders` as a nullable list of nullable
  // `Order`, so both the list and its entries are guarded before `.map`.
  if (!Array.isArray(orders)) return []

  return orders.filter(Boolean).map((order) => ({
    id: order.id,
    createdAt: order.createdAt ?? undefined,
    itemsCount: Array.isArray(order.items) ? order.items.length : 0,
    total: order.total ?? undefined,
    status: order.status ?? undefined,
    sequence: order.sequence ?? undefined,
  }))
}

/**
 * Logs an order-history fetch failure to Datadog RUM with a structured
 * context, matching the `generateDataDogContext` + `datadogRum` pairing in
 * `hooks/summary/useGetSummary.ts`.
 *
 * For ApolloErrors it lifts `graphQLErrors[].extensions.code` and
 * `networkError.message` into separate facets so dashboards can split by
 * failure mode without parsing the raw error.
 *
 * The caller still owns the matching slice dispatch, because a first-page
 * failure and a load-more failure write different state.
 * @internal
 */
export const handleOrdersFetchError = (
  exception: unknown,
  functionName: string,
  title: string,
) => {
  const extraInfo: Record<string, unknown> = { function: functionName }

  if (exception instanceof ApolloError) {
    extraInfo.graphQLErrorCodes = exception.graphQLErrors?.map(
      (error) => error.extensions?.code,
    )
    extraInfo.graphQLErrorMessages = exception.graphQLErrors?.map(
      (error) => error.message,
    )
    extraInfo.networkErrorMessage = exception.networkError?.message
  }

  const context = generateDataDogContext({ title, extraInfo })
  datadogRum.startView(context.viewName)
  datadogRum.addError(exception, context)
}

/**
 * Reads a human-readable reason off a failed fetch, for `loadMoreError`.
 * @internal
 */
export const getOrdersErrorMessage = (exception: unknown): string => {
  if (exception instanceof Error && exception.message) return exception.message
  return 'LOAD_MORE_FAILED'
}
